import { ProductSummaryWithViewedAt } from "@/common/productSummary/types";
import { ProductCardProps } from "@/components/ProductCard/types";
import { getLevel } from "@/components/Search/utils/getLevel";

export type RecentlyViewedCardProps = ProductCardProps & {
  viewedSince?: number;
};

/**
 * Transforms a ProductSummaryWithViewedAt into ProductCardProps (extended with
 * viewedSince) for the recently viewed context. viewedSince (minutes ago) is
 * derived from the viewedAt field merged onto the product by useProductSummaries.
 */
const normalizeSlugForHref = (slug: string): string => {
  if (!slug) {
    return "/";
  }

  let normalized = slug;

  // If slug is an absolute URL, drop the origin and keep only the path/search/hash.
  try {
    const url = new URL(slug);
    normalized = `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // slug is not an absolute URL; leave it as-is.
  }

  // Remove all leading slashes to avoid '//' when we prefix with exactly one.
  normalized = normalized.replace(/^\/+/, "");

  // Ensure exactly one leading slash. If the path is empty, this yields "/".
  return `/${normalized}`;
};

export const convertToRecentlyViewedCard = (
  product: ProductSummaryWithViewedAt,
): RecentlyViewedCardProps => {
  return {
    uuid: product.productUuid,
    name: product.productName,
    href: normalizeSlugForHref(product.productSlug),
    imageUrl: product.productImageUrl,
    type: product.productType,
    partnerKey: product.partnerKeys,
    partnerFullName: product.partnerName,
    partnerLogoUrl: product.partnerLogoOverride ?? product.partnerLogoUrl,
    organizationLogoOverride: product.organizationLogoOverride,
    weeksToComplete: product.weeksToComplete ?? undefined,
    attributes: product.attributes ?? undefined,
    isPartOfProgram: product.isPartOfProgram,
    childCoursesCount:
      product.childCoursesCount ?? product.courseCount ?? undefined,
    objectId: product.objectID,
    productSource: product.productSource ?? undefined,
    product: product.product,
    activeRunKey: product.activeRunKey,
    isNew: product.isNew,
    level: getLevel(product.level || "") || undefined,
    pacing: product.flexibility as
      | "self_paced"
      | "instructor_paced"
      | undefined,
    viewedSince: Math.max(
      0,
      Math.round((Date.now() - product.viewedAt) / 60000),
    ),
  };
};
